import { useState } from "react";

import { IChangeAssigneeDialog } from "./ChangeAssigneeDialog";
import { useTranslation } from "../../localization/useTranslation";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { put } from "../../common/api/apiShared";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { apiUrls } from "@common/api/apiUrls";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { CreatedExternalUser } from "../../externalUser/createExternalUserInlineForm/CreateExternalUserInlineForm";
import { SelectableAssignee } from "../SelectableAssignee.types";
import { getChangeAssigneeRequest } from "../SelectableAssignee.utils";

export function useChangeAssigneeDialogHooks(props: IChangeAssigneeDialog) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const { permissions } = useUserContext();
    const [assignee, setAssignee] = useState(props.internalAssessmentViewModel.assignee.id);
    const [assigneeSearch, setAssigneeSearch] = useState<string>("");

    const { data, isLoading, refetch } = useDotLegalQuery<Array<SelectableAssignee>>(apiUrls().internalAssessments.selectableAssignees);

    const changeAssigneeMutation = useDotLegalMutation(changeAssigneeApi, {
        onSuccess: () => {
            snackbar.show(translateString("contactChanged"));
            props.onChangedAssignee();
        },
    });

    const changeAssigneeRequest = getChangeAssigneeRequest(data, assignee);

    const onChangeAssigneeClick = () => {
        changeAssigneeMutation.mutateAsync();
    };

    // Selecting the new recipient only holds once it is in the list the save request is built from, so the
    // refreshed list has to land first.
    const onRecipientCreated = async (created: CreatedExternalUser) => {
        await refetch();
        setAssignee(created.id);
    };

    function changeAssigneeApi() {
        return put(`/internalAssessments/${props.internalAssessmentViewModel.id}/changeAssignee`, changeAssigneeRequest);
    }

    return {
        data,
        isLoading,
        isChanging: changeAssigneeMutation.isLoading,
        assignee,
        setAssignee,
        onChangeAssigneeClick,
        canChangeAssignee: changeAssigneeRequest !== undefined,
        assigneeSearch,
        setAssigneeSearch,
        // Creating a recipient is a user-management action, so without that permission only the existing
        // assignees are offered.
        canCreateRecipients: permissions.canManageUsers,
        existingAssigneeEmails: (data ?? []).map((selectable) => selectable.email),
        onRecipientCreated,
    };
}
